// packages/web/src/components/AdminSettings.tsx
import React, { useState, useEffect } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useSiteConfig } from '../context/SiteConfigContext'
import {
  Sliders,
  Image as ImageIcon,
  Sparkles,
  Mail,
  MapPin,
  Save,
  CheckCircle2,
  Upload,
  Award,
  Star,
  ShieldCheck,
  Tag,
  Grid,
} from 'lucide-react'
import { apiUpload } from '../lib/api'
import { SiteConfigInput } from '@buddys-choice/shared'
import type { SiteConfigInput as SiteConfigInputType } from '@buddys-choice/shared'
import { SearchableSelect } from './SearchableSelect'

const GST_OPTIONS = [
  { value: 'false', label: 'Disabled — hide GST on storefront' },
  { value: 'true', label: 'Enabled — show GST & tax lines' },
]

export const AdminSettings: React.FC = () => {
  const { config, updateConfig } = useSiteConfig()
  const [uploadingShowcase, setUploadingShowcase] = useState(false)
  const [successMsg, setSuccessMsg] = useState('')
  const [errorMsg, setErrorMsg] = useState('')

  const toFormValues = (c: typeof config): SiteConfigInputType => ({
    ...c,
    gst_enabled: c.gst_enabled === 'true' ? 'true' : 'false',
  })

  const {
    register,
    handleSubmit: handleFormSubmit,
    control,
    reset,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<SiteConfigInputType>({
    resolver: zodResolver(SiteConfigInput) as import('react-hook-form').Resolver<SiteConfigInputType>,
    defaultValues: toFormValues(config),
  })

  useEffect(() => {
    reset(toFormValues(config))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config])

  const watchedGstEnabled = watch('gst_enabled')

  const handleFileUpload = async (file: File, targetField: 'hero_showcase_image') => {
    try {
      setUploadingShowcase(true)

      const form = new FormData()
      form.append('file', file)

      const res = await apiUpload<{ success: boolean; data: { url: string } }>('uploads', form)
      const uploadedUrl = (res as any)?.data?.url || (res as any)?.url
      if (uploadedUrl) {
        setValue(targetField, uploadedUrl, { shouldValidate: true })
        setSuccessMsg(`Image uploaded successfully!`)
        setTimeout(() => setSuccessMsg(''), 3000)
      }
    } catch (err: unknown) {
      setErrorMsg((err as Error).message || 'Image upload failed.')
      setTimeout(() => setErrorMsg(''), 4000)
    } finally {
      setUploadingShowcase(false)
    }
  }

  const onSubmit = async (data: SiteConfigInputType) => {
    setSuccessMsg('')
    setErrorMsg('')

    const ok = await updateConfig(data)
    if (ok) {
      setSuccessMsg('All store dynamic texts, headlines, badges & images saved successfully!')
      setTimeout(() => setSuccessMsg(''), 4000)
    } else {
      setErrorMsg('Failed to save settings to D1 database.')
    }
  }

  return (
    <div className="space-y-8 animate-fadeIn max-w-5xl mx-auto">
      {/* Header Bar */}
      <div className="flex items-center justify-between border-b border-[var(--gold-main)]/20 pb-4">
        <div>
          <h2 className="text-xl sm:text-2xl font-title font-bold text-[var(--text-primary)] flex items-center gap-2">
            <Sliders className="w-6 h-6 text-[var(--gold-main)]" />
            Dynamic Store Content &amp; Branding Settings
          </h2>
          <p className="text-xs text-[var(--text-muted)] mt-1">
            Configure every text, banner headline, CTA label, trust badge, showcase image &amp; contact detail across the store.
          </p>
        </div>
        <button
          onClick={handleFormSubmit(onSubmit)}
          disabled={isSubmitting}
          className="btn-gold text-xs py-2.5 px-6 font-bold flex items-center gap-2 shadow-lg"
        >
          {isSubmitting ? (
            <div className="w-4 h-4 border-2 border-slate-950 border-t-transparent rounded-full animate-spin" />
          ) : (
            <>
              <Save className="w-4 h-4" />
              <span>Save All Settings</span>
            </>
          )}
        </button>
      </div>

      {/* Notifications */}
      {successMsg && (
        <div className="p-4 bg-emerald-500/10 border border-emerald-500/30 rounded-xl text-emerald-400 text-xs flex items-center gap-2">
          <CheckCircle2 className="w-4 h-4 shrink-0" />
          <span>{successMsg}</span>
        </div>
      )}

      {errorMsg && (
        <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-xl text-red-400 text-xs">
          {errorMsg}
        </div>
      )}

      <form onSubmit={handleFormSubmit(onSubmit)} className="space-y-6">
        <input type="hidden" {...register('logo_url')} />
        <input type="hidden" {...register('favicon_url')} />
        {/* SECTION 1: BRAND IDENTITY */}
        <div className="glass-luxury-card p-6 space-y-4">
          <h3 className="text-xs font-title font-bold text-[var(--gold-main)] uppercase tracking-wider flex items-center gap-2 border-b border-[var(--border)] pb-3">
            <ImageIcon className="w-4 h-4" /> Brand Identity
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-semibold text-[var(--text-primary)] uppercase tracking-wider mb-2 block">Brand Name</label>
              <input
                type="text"
                {...register('brand_name')}
                aria-invalid={!!errors.brand_name}
                className="w-full bg-[var(--bg-main)] border border-[var(--border)] focus:border-[var(--gold-main)] rounded-xl py-3 px-4 text-xs text-[var(--text-primary)] outline-none"
              />
              {errors.brand_name && (
                <span className="text-[11px] text-red-400 mt-1 block">{errors.brand_name.message}</span>
              )}
            </div>
            <div>
              <label className="text-xs font-semibold text-[var(--text-primary)] uppercase tracking-wider mb-2 block">Brand Tagline</label>
              <input
                type="text"
                {...register('brand_tagline')}
                aria-invalid={!!errors.brand_tagline}
                className="w-full bg-[var(--bg-main)] border border-[var(--border)] focus:border-[var(--gold-main)] rounded-xl py-3 px-4 text-xs text-[var(--text-primary)] outline-none"
              />
              {errors.brand_tagline && (
                <span className="text-[11px] text-red-400 mt-1 block">{errors.brand_tagline.message}</span>
              )}
            </div>
          </div>
        </div>

        {/* SECTION 2: HERO BANNER TEXT & SHOWCASE CARD */}
        <div className="glass-luxury-card p-6 space-y-4">
          <h3 className="text-xs font-title font-bold text-[var(--gold-main)] uppercase tracking-wider flex items-center gap-2 border-b border-[var(--border)] pb-3">
            <Sparkles className="w-4 h-4" /> Storefront Hero Banner &amp; Featured Showcase
          </h3>

          <div className="space-y-4">
            <div>
              <label className="text-xs font-semibold text-[var(--text-primary)] uppercase tracking-wider mb-2 block">Hero Headline Line 1</label>
              <input
                type="text"
                {...register('hero_title_line1')}
                className="w-full bg-[var(--bg-main)] border border-[var(--border)] focus:border-[var(--gold-main)] rounded-xl py-3 px-4 text-xs text-[var(--text-primary)] outline-none"
              />
            </div>

            <div>
              <label className="text-xs font-semibold text-[var(--text-primary)] uppercase tracking-wider mb-2 block">Hero Headline Line 2 (Golden Gradient)</label>
              <input
                type="text"
                {...register('hero_title_line2')}
                className="w-full bg-[var(--bg-main)] border border-[var(--border)] focus:border-[var(--gold-main)] rounded-xl py-3 px-4 text-xs text-[var(--gold-main)] font-bold outline-none"
              />
            </div>

            <div>
              <label className="text-xs font-semibold text-[var(--text-primary)] uppercase tracking-wider mb-2 block">Hero Subtext Paragraph</label>
              <textarea
                {...register('hero_subtext')}
                rows={3}
                className="w-full bg-[var(--bg-main)] border border-[var(--border)] focus:border-[var(--gold-main)] rounded-xl py-3 px-4 text-xs text-[var(--text-primary)] outline-none"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-semibold text-[var(--text-primary)] uppercase tracking-wider mb-2 block">Primary CTA Button</label>
                <input
                  type="text"
                  {...register('hero_cta_primary')}
                  className="w-full bg-[var(--bg-main)] border border-[var(--border)] rounded-xl py-2.5 px-3 text-xs text-[var(--text-primary)] outline-none"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-[var(--text-primary)] uppercase tracking-wider mb-2 block">Secondary CTA Button</label>
                <input
                  type="text"
                  {...register('hero_cta_secondary')}
                  className="w-full bg-[var(--bg-main)] border border-[var(--border)] rounded-xl py-2.5 px-3 text-xs text-[var(--text-primary)] outline-none"
                />
              </div>
            </div>

            {/* Showcase Card Configuration */}
            <div className="pt-4 border-t border-[var(--border)] space-y-4">
              <label className="text-xs font-bold text-[var(--gold-main)] uppercase tracking-wider block">
                Right Showcase Card Details
              </label>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className="text-[11px] text-[var(--text-muted)] block mb-1">Showcase Badge</label>
                  <input
                    type="text"
                    {...register('hero_showcase_badge')}
                    className="w-full bg-[var(--bg-main)] border border-[var(--border)] rounded-xl py-2 px-3 text-xs text-[var(--text-primary)] outline-none"
                  />
                </div>

                <div>
                  <label className="text-[11px] text-[var(--text-muted)] block mb-1">Showcase Item Title</label>
                  <input
                    type="text"
                    {...register('hero_showcase_title')}
                    className="w-full bg-[var(--bg-main)] border border-[var(--border)] rounded-xl py-2 px-3 text-xs text-[var(--text-primary)] outline-none"
                  />
                </div>

                <div>
                  <label className="text-[11px] text-[var(--text-muted)] block mb-1">Showcase Price</label>
                  <input
                    type="text"
                    {...register('hero_showcase_price')}
                    className="w-full bg-[var(--bg-main)] border border-[var(--border)] rounded-xl py-2 px-3 text-xs text-[var(--gold-main)] font-bold outline-none"
                  />
                </div>
              </div>

              <div>
                <label className="text-[11px] text-[var(--text-muted)] block mb-1">Showcase Image URL &amp; Upload</label>
                <div className="flex items-center gap-3">
                  <input
                    type="text"
                    {...register('hero_showcase_image')}
                    aria-invalid={!!errors.hero_showcase_image}
                    className="flex-1 bg-[var(--bg-main)] border border-[var(--border)] rounded-xl py-2 px-3 text-xs text-[var(--text-primary)] font-mono outline-none"
                  />
                  <label className="btn-gold-outline text-xs py-2 px-4 flex items-center gap-1.5 cursor-pointer shrink-0">
                    <Upload className="w-3.5 h-3.5" />
                    <span>{uploadingShowcase ? 'Uploading...' : 'Upload Image'}</span>
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => {
                        const file = e.target.files?.[0]
                        if (file) handleFileUpload(file, 'hero_showcase_image')
                      }}
                    />
                  </label>
                </div>
                {errors.hero_showcase_image && (
                  <span className="text-[11px] text-red-400 mt-1 block">{errors.hero_showcase_image.message}</span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* SECTION 3: TRUST STATS & SECTION HEADINGS */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Trust Stats Badges */}
          <div className="glass-luxury-card p-6 space-y-4">
            <h3 className="text-xs font-title font-bold text-[var(--gold-main)] uppercase tracking-wider flex items-center gap-2 border-b border-[var(--border)] pb-3">
              <Star className="w-4 h-4" /> Hero Trust Badges (3 Badges)
            </h3>

            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-2">
                <input
                  type="text"
                  {...register('hero_stat1_value')}
                  placeholder="e.g. 1 Year"
                  className="bg-[var(--bg-main)] border border-[var(--border)] rounded-xl py-2 px-3 text-xs text-[var(--text-primary)] font-bold outline-none"
                />
                <input
                  type="text"
                  {...register('hero_stat1_label')}
                  placeholder="e.g. Color Warranty"
                  className="bg-[var(--bg-main)] border border-[var(--border)] rounded-xl py-2 px-3 text-xs text-[var(--text-muted)] outline-none"
                />
              </div>

              <div className="grid grid-cols-2 gap-2">
                <input
                  type="text"
                  {...register('hero_stat2_value')}
                  placeholder="e.g. 4.9 ★"
                  className="bg-[var(--bg-main)] border border-[var(--border)] rounded-xl py-2 px-3 text-xs text-[var(--text-primary)] font-bold outline-none"
                />
                <input
                  type="text"
                  {...register('hero_stat2_label')}
                  placeholder="e.g. 50,000+ Reviews"
                  className="bg-[var(--bg-main)] border border-[var(--border)] rounded-xl py-2 px-3 text-xs text-[var(--text-muted)] outline-none"
                />
              </div>

              <div className="grid grid-cols-2 gap-2">
                <input
                  type="text"
                  {...register('hero_stat3_value')}
                  placeholder="e.g. Insured"
                  className="bg-[var(--bg-main)] border border-[var(--border)] rounded-xl py-2 px-3 text-xs text-[var(--text-primary)] font-bold outline-none"
                />
                <input
                  type="text"
                  {...register('hero_stat3_label')}
                  placeholder="e.g. Express COD"
                  className="bg-[var(--bg-main)] border border-[var(--border)] rounded-xl py-2 px-3 text-xs text-[var(--text-muted)] outline-none"
                />
              </div>
            </div>
          </div>

          {/* Homepage Section Headings */}
          <div className="glass-luxury-card p-6 space-y-4">
            <h3 className="text-xs font-title font-bold text-[var(--gold-main)] uppercase tracking-wider flex items-center gap-2 border-b border-[var(--border)] pb-3">
              <Grid className="w-4 h-4" /> Homepage Section Headings
            </h3>

            <div className="space-y-3">
              <div>
                <label className="text-[11px] text-[var(--text-muted)] block mb-1">Category Section Title</label>
                <input
                  type="text"
                  {...register('category_section_title')}
                  className="w-full bg-[var(--bg-main)] border border-[var(--border)] rounded-xl py-2 px-3 text-xs text-[var(--text-primary)] outline-none"
                />
              </div>

              <div>
                <label className="text-[11px] text-[var(--text-muted)] block mb-1">Category Section Subtitle</label>
                <input
                  type="text"
                  {...register('category_section_subtitle')}
                  className="w-full bg-[var(--bg-main)] border border-[var(--border)] rounded-xl py-2 px-3 text-xs text-[var(--text-muted)] outline-none"
                />
              </div>

              <div>
                <label className="text-[11px] text-[var(--text-muted)] block mb-1">Catalogue Section Title</label>
                <input
                  type="text"
                  {...register('catalogue_section_title')}
                  className="w-full bg-[var(--bg-main)] border border-[var(--border)] rounded-xl py-2 px-3 text-xs text-[var(--text-primary)] outline-none"
                />
              </div>

              <div>
                <label className="text-[11px] text-[var(--text-muted)] block mb-1">Catalogue Section Subtitle</label>
                <input
                  type="text"
                  {...register('catalogue_section_subtitle')}
                  className="w-full bg-[var(--bg-main)] border border-[var(--border)] rounded-xl py-2 px-3 text-xs text-[var(--text-muted)] outline-none"
                />
              </div>
            </div>
          </div>
        </div>

        {/* SECTION 4: VIP CIRCLE & VALUE PROPOSITION BADGES */}
        <div className="glass-luxury-card p-6 space-y-4">
          <h3 className="text-xs font-title font-bold text-[var(--gold-main)] uppercase tracking-wider flex items-center gap-2 border-b border-[var(--border)] pb-3">
            <Tag className="w-4 h-4" /> VIP Newsletter &amp; Store Value Propositions (4 Grid Badges)
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-[11px] text-[var(--text-muted)] block mb-1">VIP Badge Text</label>
              <input
                type="text"
                {...register('vip_badge_text')}
                className="w-full bg-[var(--bg-main)] border border-[var(--border)] rounded-xl py-2 px-3 text-xs text-[var(--text-primary)] outline-none"
              />
            </div>
            <div>
              <label className="text-[11px] text-[var(--text-muted)] block mb-1">VIP Banner Title</label>
              <input
                type="text"
                {...register('vip_title')}
                className="w-full bg-[var(--bg-main)] border border-[var(--border)] rounded-xl py-2 px-3 text-xs text-[var(--text-primary)] outline-none"
              />
            </div>
          </div>

          <div className="pt-2 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1">
              <label className="text-[11px] font-bold text-[var(--text-primary)] block">Proposition 1 (Quality)</label>
              <input
                type="text"
                {...register('prop1_title')}
                className="w-full bg-[var(--bg-main)] border border-[var(--border)] rounded-xl py-1.5 px-3 text-xs text-[var(--text-primary)] outline-none"
              />
              <input
                type="text"
                {...register('prop1_subtext')}
                className="w-full bg-[var(--bg-main)] border border-[var(--border)] rounded-xl py-1.5 px-3 text-[11px] text-[var(--text-muted)] outline-none"
              />
            </div>

            <div className="space-y-1">
              <label className="text-[11px] font-bold text-[var(--text-primary)] block">Proposition 2 (Warranty)</label>
              <input
                type="text"
                {...register('prop2_title')}
                className="w-full bg-[var(--bg-main)] border border-[var(--border)] rounded-xl py-1.5 px-3 text-xs text-[var(--text-primary)] outline-none"
              />
              <input
                type="text"
                {...register('prop2_subtext')}
                className="w-full bg-[var(--bg-main)] border border-[var(--border)] rounded-xl py-1.5 px-3 text-[11px] text-[var(--text-muted)] outline-none"
              />
            </div>

            <div className="space-y-1">
              <label className="text-[11px] font-bold text-[var(--text-primary)] block">Proposition 3 (Shipping)</label>
              <input
                type="text"
                {...register('prop3_title')}
                className="w-full bg-[var(--bg-main)] border border-[var(--border)] rounded-xl py-1.5 px-3 text-xs text-[var(--text-primary)] outline-none"
              />
              <input
                type="text"
                {...register('prop3_subtext')}
                className="w-full bg-[var(--bg-main)] border border-[var(--border)] rounded-xl py-1.5 px-3 text-[11px] text-[var(--text-muted)] outline-none"
              />
            </div>

            {watchedGstEnabled === 'true' && (
              <div className="space-y-1">
                <label className="text-[11px] font-bold text-[var(--text-primary)] block">Proposition 4 (GST &amp; Tax)</label>
                <input
                  type="text"
                  {...register('prop4_title')}
                  className="w-full bg-[var(--bg-main)] border border-[var(--border)] rounded-xl py-1.5 px-3 text-xs text-[var(--text-primary)] outline-none"
                />
                <input
                  type="text"
                  {...register('prop4_subtext')}
                  className="w-full bg-[var(--bg-main)] border border-[var(--border)] rounded-xl py-1.5 px-3 text-[11px] text-[var(--text-muted)] outline-none"
                />
              </div>
            )}
          </div>
        </div>

        {/* SECTION 5: COMPANY MASTER & CONTACT */}
        <div className="glass-luxury-card p-6 space-y-4">
          <h3 className="text-xs font-title font-bold text-[var(--gold-main)] uppercase tracking-wider flex items-center gap-2 border-b border-[var(--border)] pb-3">
            <ShieldCheck className="w-4 h-4" /> Company Master &amp; Legal
          </h3>

          <p className="text-[11px] text-[var(--text-muted)]">
            GST and tax invoicing appear on the storefront only when enabled here with a valid GSTIN.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-[11px] text-[var(--text-muted)] block mb-1">Registered Company Name</label>
              <input
                type="text"
                {...register('company_legal_name')}
                placeholder="Buddy's Choice Jewellery Pvt. Ltd."
                className="w-full bg-[var(--bg-main)] border border-[var(--border)] rounded-xl py-2 px-3 text-xs text-[var(--text-primary)] outline-none"
              />
            </div>

            <div>
              <label className="text-[11px] text-[var(--text-muted)] block mb-1">GST Invoicing</label>
              <Controller
                control={control}
                name="gst_enabled"
                render={({ field }) => (
                  <SearchableSelect
                    value={field.value}
                    onChange={field.onChange}
                    options={GST_OPTIONS}
                    placeholder="GST invoicing"
                    sortAlphabetically={false}
                    aria-label="GST invoicing"
                    className="w-full"
                  />
                )}
              />
            </div>
          </div>

          {watchedGstEnabled === 'true' && (
            <div>
              <label className="text-[11px] text-[var(--text-muted)] block mb-1">GSTIN Number</label>
              <input
                type="text"
                {...register('gstin_number')}
                placeholder="33AAAAA0000A1Z5"
                aria-invalid={!!errors.gstin_number}
                className="w-full sm:w-1/2 bg-[var(--bg-main)] border border-[var(--border)] rounded-xl py-2 px-3 text-xs text-[var(--text-primary)] font-mono outline-none"
              />
              {errors.gstin_number && (
                <span className="text-[11px] text-red-400 mt-1 block">{errors.gstin_number.message}</span>
              )}
            </div>
          )}

          <h4 className="text-[11px] font-bold text-[var(--text-primary)] pt-2">Store Contact</h4>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-[11px] text-[var(--text-muted)] block mb-1">Support Phone</label>
              <input
                type="text"
                {...register('contact_phone')}
                className="w-full bg-[var(--bg-main)] border border-[var(--border)] rounded-xl py-2 px-3 text-xs text-[var(--text-primary)] outline-none"
              />
            </div>

            <div>
              <label className="text-[11px] text-[var(--text-muted)] block mb-1">Support Email</label>
              <input
                type="email"
                {...register('contact_email')}
                aria-invalid={!!errors.contact_email}
                className="w-full bg-[var(--bg-main)] border border-[var(--border)] rounded-xl py-2 px-3 text-xs text-[var(--text-primary)] outline-none"
              />
              {errors.contact_email && (
                <span className="text-[11px] text-red-400 mt-1 block">{errors.contact_email.message}</span>
              )}
            </div>
          </div>

          <div>
            <label className="text-[11px] text-[var(--text-muted)] block mb-1">Store Address</label>
            <input
              type="text"
              {...register('store_address')}
              className="w-full bg-[var(--bg-main)] border border-[var(--border)] rounded-xl py-2 px-3 text-xs text-[var(--text-primary)] outline-none"
            />
          </div>
        </div>

        {/* SECTION 6: NAVIGATION & FOOTER (JSON) */}
        <div className="glass-luxury-card p-6 space-y-4">
          <h3 className="text-xs font-title font-bold text-[var(--gold-main)] uppercase tracking-wider flex items-center gap-2 border-b border-[var(--border)] pb-3">
            <Grid className="w-4 h-4" /> Navigation, Categories &amp; Footer Links
          </h3>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="text-[11px] text-[var(--text-muted)] block mb-1">Footer Collections Title</label>
              <input
                type="text"
                {...register('footer_collections_title')}
                className="w-full bg-[var(--bg-main)] border border-[var(--border)] rounded-xl py-2 px-3 text-xs text-[var(--text-primary)] outline-none"
              />
            </div>
            <div>
              <label className="text-[11px] text-[var(--text-muted)] block mb-1">Footer Care Title</label>
              <input
                type="text"
                {...register('footer_care_title')}
                className="w-full bg-[var(--bg-main)] border border-[var(--border)] rounded-xl py-2 px-3 text-xs text-[var(--text-primary)] outline-none"
              />
            </div>
          </div>

          <div>
            <label className="text-[11px] text-[var(--text-muted)] block mb-1">Footer Brand Description</label>
            <textarea
              {...register('footer_brand_blurb')}
              rows={2}
              placeholder="Leave empty to auto-generate from brand name & tagline"
              className="w-full bg-[var(--bg-main)] border border-[var(--border)] rounded-xl py-2 px-3 text-xs text-[var(--text-primary)] outline-none resize-none"
            />
          </div>

          <div className="rounded-xl border border-[var(--border)] bg-[var(--bg-surface-2)]/50 p-4">
            <p className="text-xs text-[var(--text-muted)]">
              Store categories are now managed in{' '}
              <a href="/admin/categories" className="text-[var(--gold-main)] font-semibold hover:underline">
                Admin → Categories
              </a>
              . Legacy JSON below is only used if the categories API is unavailable.
            </p>
            <label className="text-[11px] text-[var(--text-muted)] block mb-1 mt-3">
              Legacy Store Categories JSON <span className="text-[var(--text-faint)]">(fallback only)</span>
            </label>
            <textarea
              {...register('categories_json')}
              rows={6}
              placeholder='[{"slug":"rings","label":"Rings & Solitaires","navLabel":"Rings","imageUrl":"https://..."}]'
              aria-invalid={!!errors.categories_json}
              className="w-full bg-[var(--bg-main)] border border-[var(--border)] rounded-xl py-2 px-3 text-[11px] font-mono text-[var(--text-primary)] outline-none resize-y"
            />
            {errors.categories_json && (
              <span className="text-[11px] text-red-400 mt-1 block">{errors.categories_json.message}</span>
            )}
          </div>

          <div>
            <label className="text-[11px] text-[var(--text-muted)] block mb-1">
              Footer Care Links JSON <span className="text-[var(--text-faint)]">(label, href)</span>
            </label>
            <textarea
              {...register('footer_care_links_json')}
              rows={4}
              placeholder='[{"label":"Track Order","href":"#"}]'
              aria-invalid={!!errors.footer_care_links_json}
              className="w-full bg-[var(--bg-main)] border border-[var(--border)] rounded-xl py-2 px-3 text-[11px] font-mono text-[var(--text-primary)] outline-none resize-y"
            />
            {errors.footer_care_links_json && (
              <span className="text-[11px] text-red-400 mt-1 block">{errors.footer_care_links_json.message}</span>
            )}
          </div>
        </div>

        {/* Submit Action */}
        <div className="flex justify-end pt-4">
          <button
            type="submit"
            disabled={isSubmitting}
            className="btn-gold text-xs py-3 px-8 font-bold flex items-center gap-2 shadow-xl"
          >
            {isSubmitting ? (
              <div className="w-4 h-4 border-2 border-slate-950 border-t-transparent rounded-full animate-spin" />
            ) : (
              <>
                <Save className="w-4 h-4" />
                <span>Save All Settings</span>
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  )
}
